import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../api/reports.api';
import type { ReportsFilters } from '../types/reports.types';
import { getStageFromStatus } from '../utils/statusMapping';
import { parseDate } from '../utils/dateUtils';

interface UseReportsProps {
  clientId: string;
  initialFilter?: string;
}

export const useReports = ({ clientId, initialFilter }: UseReportsProps) => {
  const [filters, setFilters] = useState<ReportsFilters>({
    searchQuery: '',
    sortBy: 'progress',
    sortOrder: 'asc',
    activeFilter: 'all', // No urgent filter for banque role
    dateFrom: undefined,
    dateTo: undefined
  });


  // Fetch reports data with only client_id - all filtering happens client-side
  const { data: reportsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['banque-reports-data', clientId],
    queryFn: () => reportsApi.fetchReports({ clientId }),
    enabled: !!clientId,
    refetchInterval: 60000,
  });

  
  // The API returns { success: true, data: { reports: [...], pagination: {...} }, timestamp: ... }
  // So we need to access the nested data property
  const reports = reportsResponse?.data?.reports || [];
  const pagination = reportsResponse?.data?.pagination;

  // Update a single filter
  const updateFilter = <K extends keyof ReportsFilters>(
    key: K,
    value: ReportsFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Filter and sort reports
  const filteredReports = useMemo(() => {
    let result = [...reports];

    // Apply active filter
    switch (filters.activeFilter) {
      case 'paid':
        result = result.filter(r => r.payment?.status === 'paid');
        break;
      case 'unpaid':
        result = result.filter(r => r.payment?.status === 'unpaid');
        break;
    }

    // Apply search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(r => 
        r.title?.toLowerCase().includes(query) ||
        r.client?.name?.toLowerCase().includes(query) ||
        r.expert?.name?.toLowerCase().includes(query) ||
        r.property?.address?.some(addr => addr.toLowerCase().includes(query)) ||
        String(r.id).includes(query)
      );
    }

    // Apply date filters
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      result = result.filter(r => {
        const date = parseDate(r.created_date);
        return date && date >= fromDate;
      });
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      result = result.filter(r => {
        const date = parseDate(r.created_date);
        return date && date <= toDate;
      });
    }

    // Sort results
    try {
      result.sort((a, b) => {
        let comparison = 0;
        
        switch (filters.sortBy) {
          case 'date': {
            const dateA = parseDate(a.created_date)?.getTime() || 0;
            const dateB = parseDate(b.created_date)?.getTime() || 0;
            comparison = dateA - dateB;
            break;
          }
          case 'amount':
            comparison = (a.amount || 0) - (b.amount || 0);
            break;
          case 'id':
            comparison = String(a.id).localeCompare(String(b.id));
            break;
          case 'progress': {
            const stageA = getStageFromStatus(a.status);
            const stageB = getStageFromStatus(b.status);
            comparison = stageA - stageB;

            // Secondary sort by date if same progress
            if (comparison === 0) {
              const dateA = parseDate(a.created_date)?.getTime() || 0;
              const dateB = parseDate(b.created_date)?.getTime() || 0;
              comparison = dateB - dateA; // Newer first
            }
            break;
          }
        }

        return filters.sortOrder === 'asc' ? comparison : -comparison;
      });
    } catch (error) {
      console.error('Error sorting reports:', error);
    }

    return result;
  }, [reports, filters]);

  return {
    reports: filteredReports,
    pagination,
    isLoading,
    error,
    filters,
    updateFilter,
    refetch
  };
};